import { newLookLiftCard } from "features/portfolio/NewLook/styles.ts";
import React, { useMemo } from "react";
import styled from "styled-components";
import { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { PortfolioAccountData } from "@shared/features/portfolio/types";
import { useAccountCard } from "@shared/features/portfolio/hooks";
import { selectPortfolioPrecision } from "@shared/slices/portfolio.slice";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { DISPLAY_CURRENCIES, ICON_TYPES } from "@shared/common/constants";
import {
  ClientStatus,
  ConsolidatedAccountData,
  LanguageNameSpaces,
  PrecisionType,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { round } from "@shared/utils/numbers";
import IconWithFallback from "Global/IconWithFallback";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import Tooltip from "common/Tooltips/Tooltip";
import Skeleton from "common/NewLook/Skeleton.tsx";
import {
  directionColor,
  getNewLookTokens,
  NEW_LOOK_FIGURE_WEIGHT,
  NEW_LOOK_RADIUS,
  newLookPhoneType,
  newLookType,
} from "common/NewLook/tokens.ts";
import AccountActions from "features/configuration/accounts/AccountActions";
import AccountStatusTag from "features/configuration/accounts/AccountConfigurationDrawer/AccountStatusTag";
import WhitelistTag from "features/configuration/accounts/AccountConfigurationDrawer/WhitelistTag";
import NewLookSparkline from "features/portfolio/NewLook/NewLookSparkline.tsx";
import NewLookCoinAllocation from "features/portfolio/NewLook/NewLookCoinAllocation.tsx";
import { formatSignedPercent } from "features/portfolio/NewLook/format.ts";
import { TIME_RANGE_1_DAY } from "common/constants";
import {
  NEW_LOOK_PHONE_MAX_WIDTH,
  phoneMaxWidth,
} from "common/NewLook/layout.ts";
import { NEW_LOOK_ACCOUNT_CARD_ID, NEW_LOOK_COIN_ALLOCATION_ID } from "testIds";
import AutoSizeText from "common/Text/AutoSizeText.tsx";

const TOTAL_MIN_FONT_SIZE = 12;

interface NewLookAccountCardProps {
  account: PortfolioAccountData;
  loading?: boolean;
  openAccountExtraDetails?: (accountData: ConsolidatedAccountData) => void;
}

/**
 * New-look account card: identity and status on top, the balance as the
 * card's figure with its 24h trend, and the coins as the Dashboard's slim
 * allocation bar.
 */
const NewLookAccountCard = ({
  account,
  loading,
  openAccountExtraDetails,
}: NewLookAccountCardProps) => {
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { accountCoins } = useAccountCard({ account });
  const isInvalid = account.client_status !== ClientStatus.ACTIVE;
  const isBtc = displayCurrency === DISPLAY_CURRENCIES.BTC;
  const valueIndex = isBtc ? 2 : 1;

  const { values, change } = useMemo(() => {
    const graphData = account.graph_data as unknown as
      | ({ change?: number[] | number } & Record<string, unknown>)
      | undefined;
    const points =
      (graphData?.[TIME_RANGE_1_DAY] as number[][] | undefined) || [];
    const rawChange = Array.isArray(graphData?.change)
      ? graphData?.change[valueIndex - 1]
      : graphData?.change;
    return {
      values: points.map((point) => Number(point[valueIndex])),
      change:
        points.length > 1 && rawChange !== undefined
          ? round((rawChange || 0) * 100, 2)
          : undefined,
    };
  }, [account.graph_data, valueIndex]);

  const getAmount = (value: number) => (
    <TextWithPrecisionAndIcon
      precisionSelector={selectPortfolioPrecision}
      precisionType={PrecisionType.AMOUNT}
      text={value}
      round={true}
    />
  );

  const getValue = (value: number, skeleton: React.JSX.Element) => {
    if (loading) return skeleton;
    if (isInvalid) return t("common.n_a");
    return getAmount(value);
  };

  const open = () => openAccountExtraDetails?.(account);

  return (
    <Card
      role="button"
      tabIndex={0}
      data-testid={NEW_LOOK_ACCOUNT_CARD_ID}
      aria-label={account.client_name}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === "Enter" && event.target === event.currentTarget)
          open();
      }}
    >
      <Header>
        <Logo>
          <IconWithFallback
            iconType={ICON_TYPES.EXCHANGE}
            coinOrExchangeName={account.exchange}
            pxIconSize={28}
          />
        </Logo>
        <Names>
          <Tooltip title={account.client_name}>
            <Name>{account.client_name}</Name>
          </Tooltip>
          <Meta>
            <Exchange>{account.exchange}</Exchange>
            {account.client_status && (
              <Status>
                <AccountStatusTag
                  status={account.client_status}
                  withBgColor={false}
                  size="small"
                />
              </Status>
            )}
          </Meta>
        </Names>
        <Actions onClick={(event) => event.stopPropagation()}>
          <AccountActions accountId={account.client_id} iconSize={18} />
        </Actions>
      </Header>

      <Figures>
        <Amounts>
          <Label>{t("accounts.total")}</Label>
          <Total>
            <AutoSizeText
              inheritFont={true}
              minFontSizePx={TOTAL_MIN_FONT_SIZE}
            >
              {getValue(
                isBtc ? account.total_in_btc : account.total_in_usd,
                <Skeleton width={120} height={22} style={{ margin: "2px 0" }} />
              )}
            </AutoSizeText>
          </Total>
          <Available>
            <span>{t("accounts.available")}</span>
            <strong>
              {getValue(
                isBtc ? account.available_in_btc : account.available_in_usd,
                <Skeleton width={72} height={12} />
              )}
            </strong>
          </Available>
        </Amounts>
        {!isInvalid && !loading && (
          <Trend>
            <NewLookSparkline
              values={values}
              color={directionColor(tokens, change)}
              width={96}
              height={34}
            />
            {change !== undefined && (
              <Change style={{ color: directionColor(tokens, change) }}>
                {formatSignedPercent(change)}
              </Change>
            )}
          </Trend>
        )}
      </Figures>

      {account.has_assigned_ips === false && account.is_valid && (
        <WhitelistTag
          withBgColor={false}
          animated={false}
          exchange={account.exchange}
        />
      )}

      {(loading || isInvalid || accountCoins.length > 0) && (
        <Allocation data-testid={NEW_LOOK_COIN_ALLOCATION_ID}>
          <NewLookCoinAllocation
            items={accountCoins}
            loading={loading}
            unavailable={isInvalid}
            getDetails={(coinData) => [
              {
                name: t("accounts.total"),
                value: getAmount(
                  isBtc ? coinData.total_in_btc : coinData.total_in_usd
                ),
              },
              {
                name: t("accounts.available"),
                value: getAmount(
                  isBtc ? coinData.available_in_btc : coinData.available_in_usd
                ),
              },
            ]}
          />
        </Allocation>
      )}
    </Card>
  );
};

export default React.memo(NewLookAccountCard);

/** A card-shaped placeholder while the accounts list loads. */
export const NewLookAccountCardSkeleton = () => (
  <Card as="div" aria-hidden={true} style={{ cursor: "default" }}>
    <Header>
      <Skeleton width={36} height={36} radius={NEW_LOOK_RADIUS.control} />
      <Names>
        <Skeleton width={140} height={14} />
        <Skeleton width={90} height={12} />
      </Names>
    </Header>
    <Figures>
      <Amounts>
        <Skeleton width={40} height={12} />
        <Skeleton width={120} height={22} style={{ margin: "2px 0" }} />
        <Skeleton width={110} height={12} />
      </Amounts>
      <Skeleton width={96} height={34} />
    </Figures>
    <Allocation>
      <NewLookCoinAllocation items={[]} loading={true} />
    </Allocation>
  </Card>
);

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  padding: 24px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  cursor: pointer;
  outline: none;
  ${newLookLiftCard};
  @media (max-width: ${NEW_LOOK_PHONE_MAX_WIDTH}) {
    padding: 14px 16px;
    gap: 12px;
  }
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;
const Logo = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  /* The exchange logo on its own: no tile around it. */
  width: 32px;
  height: 32px;
`;
const Names = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;
const Name = styled.div`
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  @media (max-width: ${phoneMaxWidth}) {
    ${newLookPhoneType.cardTitle};
  }
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;
const Exchange = styled.div`
  min-width: 0;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const Status = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  &::before {
    content: "";
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: ${(p) => getNewLookTokens(p.theme).muted};
  }
`;
const Actions = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  margin-right: -6px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Figures = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  @media (max-width: ${NEW_LOOK_PHONE_MAX_WIDTH}) {
    padding: 10px 12px;
    border-radius: ${NEW_LOOK_RADIUS.control}px;
    background: ${(p) => getNewLookTokens(p.theme).well};
  }
`;
const Amounts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const Label = styled.div`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const Total = styled.div`
  ${newLookType.figureLarge};
  /* Never above 18 inside a card; the phone figure step on a phone. */
  font-size: 18px;
  line-height: 24px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  @media (max-width: ${phoneMaxWidth}) {
    ${newLookPhoneType.figure};
  }
  * {
    font-size: inherit !important;
    font-weight: inherit !important;
    color: inherit !important;
  }
`;
const Available = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  white-space: nowrap;
  strong {
    font-weight: ${NEW_LOOK_FIGURE_WEIGHT};
    color: ${(p) => getNewLookTokens(p.theme).text};
    font-variant-numeric: tabular-nums;
  }
`;
const Trend = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
`;
const Change = styled.span`
  ${newLookType.meta};
  font-weight: ${NEW_LOOK_FIGURE_WEIGHT};
`;
const Allocation = styled.div`
  padding-top: 16px;
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  @media (max-width: ${NEW_LOOK_PHONE_MAX_WIDTH}) {
    padding-top: 0;
    border-top: none;
  }
`;
